import { useEffect, useRef, useState } from 'react';

const certifications = [
  {
    count: 4,
    platform: "Savant",
    fullName: "Savant Systems",
    label: "Certified Designers",
    badgeText: "Savant Certified",
    detail: "Programming & Blueprint Design Certification",
    icon: "ri-home-wifi-line",
  },
  {
    count: 3,
    platform: "Lutron",
    fullName: "Lutron Electronics",
    label: "Certified Programmers",
    badgeText: "Lutron Certified",
    detail: "Lighting & Shading Certification",
    icon: "ri-lightbulb-flash-line",
  },
  {
    count: 5,
    platform: "Q-SYS",
    fullName: "Q-SYS / QSC",
    label: "Certified Designers",
    badgeText: "QSC Certified",
    detail: "Level 0 Design Certification",
    icon: "ri-speaker-3-line",
  },
  {
    count: 1,
    platform: "MXnet",
    fullName: "MXnet AVoIP",
    label: "Certified Designer",
    badgeText: "MXnet Certified",
    detail: "AVoIP Certification",
    icon: "ri-router-line",
  },
  {
    count: 2,
    platform: "Crestron",
    fullName: "Crestron Electronics",
    label: "Certified Programmers",
    badgeText: "Crestron Certified",
    detail: "Control System Certification",
    icon: "ri-macbook-line",
  },
];

const trustPillars = [
  { icon: "ri-shield-check-line", label: "Manufacturer-Certified" },
  { icon: "ri-refresh-line", label: "Continuously Trained" },
  { icon: "ri-global-line", label: "Remote & On-Site Ready" },
  { icon: "ri-team-line", label: "Dedicated Support Team" },
];

function AnimatedCounter({ target }: { target: number }) {
  const [count, setCount] = useState(0);
  const ref = useRef<HTMLSpanElement>(null);
  const started = useRef(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !started.current) {
          started.current = true;
          const duration = 1400;
          const steps = 50;
          const increment = target / steps;
          let current = 0;
          const interval = setInterval(() => {
            current += increment;
            if (current >= target) {
              setCount(target);
              clearInterval(interval);
            } else {
              setCount(Math.floor(current));
            }
          }, duration / steps);
        }
      },
      { threshold: 0.4 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, [target]);

  return <span ref={ref}>{count}</span>;
}

export default function CertifiedExpertise() {
  return (
    <section className="relative overflow-hidden bg-gray-950 py-32">
      {/* ── layered background decorations ── */}
      {/* subtle dot-grid */}
      <div
        className="absolute inset-0 opacity-[0.035]"
        style={{
          backgroundImage:
            'radial-gradient(circle, rgba(255,255,255,0.8) 1px, transparent 1px)',
          backgroundSize: '32px 32px',
        }}
      />
      {/* large diagonal accent bar */}
      <div className="absolute -left-40 top-0 h-full w-[600px] bg-gradient-to-r from-[#e18033]/6 to-transparent skew-x-[-12deg] pointer-events-none" />
      {/* warm bloom top-right */}
      <div className="absolute -top-32 right-0 w-[560px] h-[560px] bg-[#e18033]/10 rounded-full blur-[120px] pointer-events-none" />
      {/* cool shadow bottom-left */}
      <div className="absolute bottom-0 left-0 w-[400px] h-[400px] bg-slate-800/60 rounded-full blur-[100px] pointer-events-none" />

      <div className="relative z-10 max-w-7xl mx-auto px-6">

        {/* ── top label ── */}
        <div className="flex items-center justify-center mb-6">
          <div className="flex items-center gap-2.5 bg-[#e18033]/10 border border-[#e18033]/25 text-[#e18033] px-5 py-2 rounded-full text-xs font-bold tracking-widest uppercase">
            <i className="ri-award-fill text-sm" />
            Certified Professionals
          </div>
        </div>

        {/* ── headline ── */}
        <div className="text-center mb-6">
          <h2 className="text-5xl md:text-6xl lg:text-7xl font-black text-white leading-[1.05] tracking-tight">
            Certified Expertise.<br />
            <span className="relative inline-block">
              <span className="relative z-10 text-transparent bg-clip-text bg-gradient-to-r from-[#e18033] via-[#f59e0b] to-[#e18033]">
                Proven Capability.
              </span>
              {/* underline accent */}
              <span className="absolute -bottom-2 left-0 w-full h-[3px] bg-gradient-to-r from-[#e18033]/0 via-[#e18033] to-[#e18033]/0 rounded-full" />
            </span>
          </h2>
        </div>

        {/* ── sub-copy ── */}
        <p className="text-center text-lg md:text-xl text-gray-400 max-w-2xl mx-auto leading-relaxed font-light mb-20">
          Our team combines design expertise with specialized platform knowledge to deliver
          thoughtfully engineered AV and automation experiences.
        </p>

        {/* ── certification cards carousel ── */}
        <div className="relative mb-16">
          <div
            className="cert-carousel flex gap-5 overflow-x-auto pb-4 snap-x snap-mandatory"
            style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
          >
            <style>{`.cert-carousel::-webkit-scrollbar { display: none; }`}</style>
          {certifications.map((cert, idx) => (
            <div
              key={idx}
              className="group relative rounded-3xl overflow-hidden border border-white/8 bg-white/[0.02] hover:border-[#e18033]/30 transition-all duration-700 hover:bg-white/[0.05] flex-shrink-0 w-[280px] snap-start"
            >
              {/* card inner glow on hover */}
              <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-700 pointer-events-none bg-gradient-to-br from-[#e18033]/8 via-transparent to-transparent" />

              {/* orange left accent bar */}
              <div className="absolute left-0 top-6 bottom-6 w-[3px] bg-gradient-to-b from-transparent via-[#e18033] to-transparent rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-500" />

              <div className="relative z-10 p-7">
                {/* top row */}
                <div className="flex items-start justify-between mb-6">
                  <div className="flex items-center gap-2 bg-[#e18033]/10 border border-[#e18033]/20 text-[#e18033] px-3 py-1 rounded-full">
                    <span className="w-1.5 h-1.5 rounded-full bg-[#e18033] animate-pulse" />
                    <span className="text-[9px] font-bold tracking-[0.15em] uppercase">{cert.badgeText}</span>
                  </div>
                  <div className="w-10 h-10 rounded-xl bg-white/5 border border-white/10 flex items-center justify-center group-hover:bg-[#e18033]/10 group-hover:border-[#e18033]/25 transition-all duration-500">
                    <i className={`${cert.icon} text-lg text-gray-400 group-hover:text-[#e18033] transition-colors duration-500`} />
                  </div>
                </div>

                {/* giant number */}
                <div className="flex items-start gap-1.5 mb-2 leading-none">
                  <span className="text-[80px] font-black text-white leading-none tabular-nums tracking-tighter drop-shadow-[0_0_60px_rgba(225,128,51,0.15)] group-hover:drop-shadow-[0_0_80px_rgba(225,128,51,0.3)] transition-all duration-700">
                    <AnimatedCounter target={cert.count} />
                  </span>
                  <span className="mt-4 text-3xl font-black text-[#e18033]/50 group-hover:text-[#e18033]/80 transition-colors duration-500">+</span>
                </div>

                {/* platform name */}
                <p className="text-2xl font-bold text-white mb-0.5">{cert.platform}</p>
                <p className="text-sm text-gray-500 font-medium mb-5">{cert.label}</p>

                {/* divider */}
                <div className="h-px bg-gradient-to-r from-transparent via-white/10 to-transparent mb-4" />

                {/* detail */}
                <div className="flex items-center gap-2.5">
                  <div className="w-6 h-6 rounded-full bg-[#e18033]/10 flex items-center justify-center flex-shrink-0">
                    <i className="ri-verified-badge-line text-[#e18033] text-xs" />
                  </div>
                  <span className="text-gray-400 text-xs">{cert.detail}</span>
                </div>
              </div>
            </div>
          ))}
          </div>
        </div>

        {/* ── horizontal trust strip ── */}
        <div className="rounded-2xl border border-white/8 bg-white/[0.02] px-8 py-6">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {trustPillars.map((p, idx) => (
              <div key={idx} className="flex flex-col items-center gap-2 text-center group/pill">
                <div className="w-10 h-10 rounded-xl bg-[#e18033]/8 border border-[#e18033]/15 flex items-center justify-center group-hover/pill:bg-[#e18033]/15 transition-colors duration-300">
                  <i className={`${p.icon} text-[#e18033]/70 group-hover/pill:text-[#e18033] text-lg transition-colors duration-300`} />
                </div>
                <span className="text-xs font-semibold text-gray-500 group-hover/pill:text-gray-300 tracking-wide transition-colors duration-300">{p.label}</span>
              </div>
            ))}
          </div>
        </div>

      </div>
    </section>
  );
}
